import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Tabs,
  Tab,
  Box,
  Checkbox,
  Typography,
  Divider,
  Paper,
  Stack,
  CircularProgress,
  Collapse,
  FormGroup,
  FormControlLabel,
  Badge,
  Alert,
} from "@mui/material";
import { MenuBook as MenuBookIcon, FilterList as FilterListIcon } from "@mui/icons-material";
import "./AddFromSearch.css";
import { fetchSources, updateSources, fetchStudyDesigns } from "../../Utils/functions";
import { useParams } from "react-router-dom";
import { DoiButton } from "../DoiButton/DoiButton";

export const AddFromSearch = ({
  open,
  onClose,
  query,
  question: initialQuestion,
  screenedArticles = [],
  onAddSources,
  status,
  setScreenedArticles,
  incomingError,
  onSearchUpdated,
  onQuestionUpdated,
  onQueryUpdated,
  searchFilters,
  onLargeSearchWarning,
}) => {
  const [searchTab, setSearchTab] = useState("question");
  const [question, setQuestion] = useState(initialQuestion || "")
  const [keyword, setKeyword] = useState(query || "")
  const [error, setError] = useState("")
  const [isArticlesLoading, setIsArticlesLoading] = useState(false)
  const [hasUpdateError, setHasUpdateError] = useState(false)
  const { id } = useParams()

  const [showFilters, setShowFilters] = useState(false)
  const [fromDate, setFromDate] = useState(searchFilters.fromDate || "")
  const [toDate, setToDate] = useState(searchFilters.toDate || "")
  const [studyDesigns, setStudyDesigns] = useState(searchFilters.studyDesigns || [])
  const [studyDesignOptions, setStudyDesignOptions] = useState([])
  const [dateError, setDateError] = useState("")
  const [abstractContains, setAbstractContains] = useState(searchFilters.abstractContains || "")
  const [abstractNotContains, setAbstractNotContains] = useState(searchFilters.abstractNotContains || "")
  const [numberOfAbstracts, setNumberOfAbstracts] = useState(searchFilters.numberOfAbstracts || "")

  useEffect(() => {
    fetchStudyDesigns().then(setStudyDesignOptions).catch(() => { })
  }, [])

  useEffect(() => {
    if (open && status === "large_search_warning") {
      setShowFilters(true);
    }
  }, [open, status])

  const dateRegex = /^\d{4}\/\d{2}\/\d{2}$/

  const validateDates = (from, to) => {
    if (from && !dateRegex.test(from)) return "From date must be in YYYY/MM/DD format"
    if (to && !dateRegex.test(to)) return "To date must be in YYYY/MM/DD format"
    if (from && to && new Date(from) > new Date(to)) return "From date cannot be after To date"
    return ""
  }

  const handleFromDateChange = (val) => {
    setFromDate(val)
    setDateError(validateDates(val, toDate))
  }

  const handleToDateChange = (val) => {
    setToDate(val)
    setDateError(validateDates(fromDate, val))
  }

  const handleStudyDesignToggle = (value) => {
    setStudyDesigns((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    )
  }

  const handleClearFilters = () => {
    setFromDate("")
    setToDate("")
    setStudyDesigns([])
    setAbstractContains("")
    setAbstractNotContains("")
    setNumberOfAbstracts("")
    setDateError("")
  }

  const activeFilterCount = (fromDate ? 1 : 0) + (toDate ? 1 : 0) + studyDesigns.length + (abstractContains ? 1 : 0) + (abstractNotContains ? 1 : 0) + (numberOfAbstracts ? 1 : 0)

  const getTrailingBooleanOperator = (value) => {
    const trimmedValue = (value || "").trim();
    if (trimmedValue.length === 0) return null;
    const booleanOperators = ["AND", "OR", "NOT"];
    const upperValue = trimmedValue.toUpperCase();
    for (const operator of booleanOperators) {
      if (upperValue === operator || upperValue.endsWith(` ${operator}`)) {
        return trimmedValue.slice(trimmedValue.length - operator.length);
      }
    }
    return null;
  };

  const isUpdateButtonEnabled = !getTrailingBooleanOperator(keyword) && question?.trim().length > 0

  useEffect(() => {
    if (initialQuestion) {
      setQuestion(initialQuestion)
    }
    if (query) {
      setKeyword(query)
    }
    if (searchFilters.fromDate) {
      setFromDate(searchFilters.fromDate)
    }
    if (searchFilters.toDate) {
      setToDate(searchFilters.toDate)
    }
    if (searchFilters.studyDesigns) {
      setStudyDesigns(searchFilters.studyDesigns)
    }
    if (searchFilters.abstractContains) {
      setAbstractContains(searchFilters.abstractContains)
    }
    if (searchFilters.abstractNotContains) {
      setAbstractNotContains(searchFilters.abstractNotContains)
    }
    if (searchFilters.numberOfAbstracts) {
      setNumberOfAbstracts(searchFilters.numberOfAbstracts)
    }
    if (incomingError) {
      setError(incomingError);
    }
  }, [initialQuestion, query, incomingError, searchFilters]);

  useEffect(() => {
    if (!isArticlesLoading || !id) return;

    const intervalId = setInterval(async () => {
      try {
        const data = await fetchSources(id);
        if (data.status) setLoadingStatus(data.status);

        if (data.status === "Gathering Sources Completed") {
          if (data.query !== keyword) {
            setKeyword(data.query);
            if (onQueryUpdated) onQueryUpdated(data.query);
          }
          if (data.question && onQuestionUpdated) {
            onQuestionUpdated(data.question);
          }
          if (data.searchFilters) {
            if (data.searchFilters.fromDate) {
              setFromDate(data.searchFilters.fromDate)
            }
            if (data.searchFilters.toDate) {
              setToDate(data.searchFilters.toDate)
            }
            if (data.searchFilters.abstractContains) {
              setAbstractContains(data.searchFilters.abstractContains)
            }
            if (data.searchFilters.abstractNotContains) {
              setAbstractNotContains(data.searchFilters.abstractNotContains)
            }
            if (data.searchFilters.studyDesignOptions) {
              setStudyDesignOptions(data.searchFilters.studyDesignOptions)
            }
          }
          if (data.screenedArticles && setScreenedArticles) {
            const sortedArticles = [...data.screenedArticles].sort(
              (a, b) => (a.gradeData?.GradeNumber || 0) - (b.gradeData?.GradeNumber || 0)
            );
            setScreenedArticles(sortedArticles);
          }
          if (onSearchUpdated) onSearchUpdated();
          setHasUpdateError(false);
          setIsArticlesLoading(false);
        } else if (data.status === "large_search_warning") {
          setIsArticlesLoading(false);
          onClose();
          if (onLargeSearchWarning) onLargeSearchWarning(data.totalAbstracts || 0);
        } else if (data.status === "Error") {
          setError(data.error || "An error occurred");
          setHasUpdateError(true);
          setIsArticlesLoading(false);
        }
      } catch (err) {
        console.error("Error fetching job status:", err);
        setError(err.message || "Failed to fetch job status");
        setHasUpdateError(true);
        setIsArticlesLoading(false);
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [isArticlesLoading, id]);

  const [loadingStatus, setLoadingStatus] = useState("");
  const [selectedSources, setSelectedSources] = useState(new Set());

  const isCompleted = status === "Gathering Sources Completed" || status === "large_search_warning";
  const hasError = status === "Error" || !!error;
  const sources =
    (isCompleted && screenedArticles.length > 0 && !hasUpdateError) ? screenedArticles : [];
  const errorMessage = error || (status === "Error" ? "An error occurred during search" : "");

  const handleTabChange = (newValue) => {
    setSearchTab(newValue);
    if (!hasUpdateError) {
      setError("");
    }
  };

  const handleSourceToggle = (index) => {
    const newSelected = new Set(selectedSources);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedSources(newSelected);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedSources(new Set(sources.map((_, index) => index)));
    } else {
      setSelectedSources(new Set());
    }
  };

  const handleAddSources = () => {
    if (selectedSources.size === 0) {
      return;
    }
    const selected = sources.filter((_, index) => selectedSources.has(index));
    if (onAddSources && selected.length > 0) {
      onAddSources(selected);
    }
    onClose();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target
    if (name === "question") {
      setQuestion(value)
      return;
    }

    if (name === "keyword") {
      setKeyword(value)
      const trailingOperator = getTrailingBooleanOperator(value)
      if (trailingOperator) {
        setError(`Query cannot end with ${trailingOperator}`)
      } else {
        setError("")
      }
    }
  }

  const handleUpdateSearchButton = async () => {
    const trailingOperator = getTrailingBooleanOperator(keyword)
    if (trailingOperator) {
      setError(`Query cannot end with ${trailingOperator}`)
      return;
    }

    setSelectedSources(new Set());
    if (!id) {
      setError("No review ID found");
      return;
    }

    setError("");
    setHasUpdateError(false);
    setLoadingStatus("");
    setIsArticlesLoading(true);

    try {
      const parsedAbstracts = parseInt(numberOfAbstracts, 10);
      const filters = {
        ...(fromDate ? { fromDate } : {}),
        ...(toDate ? { toDate } : {}),
        ...(studyDesigns.length > 0 ? { studyDesigns } : {}),
        ...(abstractContains ? { abstractContains } : {}),
        ...(abstractNotContains ? { abstractNotContains } : {}),
        ...(parsedAbstracts > 0 ? { numberOfAbstracts: parsedAbstracts } : {}),
      }
      await updateSources(id, question, keyword, filters);
    } catch (error) {
      console.error("Error updating sources:", error);
      setError(error.message || "Failed to update sources");
      setHasUpdateError(true);
      setIsArticlesLoading(false);
    }
  };

  const allSelected =
    sources.length > 0 && selectedSources.size === sources.length;
  const someSelected =
    selectedSources.size > 0 && selectedSources.size < sources.length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      className="add-from-search-dialog"
      PaperProps={{
        sx: {
          minHeight: "80vh",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" component="div">
          Select articles from MedRPT Search
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {(isCompleted || hasError) ? (
          <Box sx={{ p: 3 }}>
            <Box sx={{ mb: 2 }}>
              <Tabs
                value={searchTab}
                onChange={(e, newValue) => handleTabChange(newValue)}
                sx={{
                  mb: 1,
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#3b7b3f",
                  },
                  "& .MuiTab-root.Mui-selected": {
                    color: "#3b7b3f",
                  },
                }}
              >
                <Tab label="Question" value="question" />
                <Tab label="Keyword" value="keyword" />
              </Tabs>

              {searchTab === "question" && (
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Ask your question..."
                  name="question"
                  value={question}
                  onChange={handleInputChange}
                  variant="outlined"
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-focused fieldset": {
                        borderColor: "#3b7b3f",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#3b7b3f",
                    },
                  }}
                />
              )}
              {searchTab === "keyword" && (
                <TextField
                  fullWidth
                  placeholder="Enter query terms"
                  name="keyword"
                  value={keyword}
                  onChange={handleInputChange}
                  variant="outlined"
                  error={!!error}
                  helperText={error}
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-focused fieldset": {
                        borderColor: "#3b7b3f",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#3b7b3f",
                    },
                  }}
                  multiline
                />
              )}
              <Box sx={{ display: "flex", justifyContent: "center", gap: 1, alignItems: "center" }}>
                <Badge badgeContent={activeFilterCount} color="success" overlap="circular">
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FilterListIcon />}
                    onClick={() => setShowFilters((v) => !v)}
                    sx={{
                      borderColor: "#3b7b3f",
                      color: "#3b7b3f",
                      borderRadius: "6px",
                      fontSize: "14px",
                      fontWeight: 500,
                      padding: "10px 20px",
                      textTransform: "none",
                      "&:hover": {
                        borderColor: "#3b7b3f",
                        backgroundColor: "rgba(59,123,63,0.06)",
                      },
                    }}
                  >
                    Filters
                  </Button>
                </Badge>
                <Button
                  variant="contained"
                  size="small"
                  sx={{
                    backgroundColor: "#3b7b3f",
                    color: "#ffffff",
                    borderRadius: "6px",
                    fontSize: "14px",
                    fontWeight: 500,
                    padding: "10px 20px",
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "#3b7b3f",
                      boxShadow: "0 2px 8px #1e3e20",
                    },
                    "&:disabled": {
                      backgroundColor: "#cccccc",
                      color: "#666666",
                    },
                  }}
                  disabled={!isUpdateButtonEnabled || isArticlesLoading || !!dateError}
                  onClick={handleUpdateSearchButton}
                >
                  Update search
                </Button>
              </Box>

              {hasUpdateError && error && (
                <Alert severity="error" sx={{ mt: 1.5 }}>
                  {error}
                </Alert>
              )}

              <Collapse in={showFilters}>
                <Box className="add-from-search-filters" sx={{ mt: 2 }}>
                  <Box className="add-from-search-filters-header" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <FilterListIcon className="add-from-search-filters-header-icon" />
                      <Typography variant="subtitle2" component="div" className="add-from-search-filters-header-title">
                        Search filters
                      </Typography>
                    </Box>
                    {activeFilterCount > 0 && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleClearFilters}
                        sx={{
                          borderColor: "#3b7b3f",
                          color: "#3b7b3f",
                          textTransform: "none",
                          fontWeight: 500,
                          fontSize: "13px",
                          p: "2px 8px",
                          minWidth: 0,
                          "&:hover": { borderColor: "#3b7b3f", backgroundColor: "rgba(59,123,63,0.06)" },
                        }}
                      >
                        Clear all
                      </Button>
                    )}
                  </Box>
                  <Box className="add-from-search-filters-body">
                    <Stack spacing={3}>
                      <Stack spacing={1.25}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          Publication date range
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Search results by publication date (YYYY/MM/DD).
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 2,
                            flexWrap: "wrap",
                          }}
                        >
                          <TextField
                            label="From date"
                            placeholder="YYYY/MM/DD"
                            value={fromDate}
                            onChange={(e) => handleFromDateChange(e.target.value)}
                            size="small"
                            sx={{
                              flex: "1 1 160px",
                              minWidth: 0,
                            }}
                          />
                          <TextField
                            label="To date"
                            placeholder="YYYY/MM/DD"
                            value={toDate}
                            onChange={(e) => handleToDateChange(e.target.value)}
                            size="small"
                            sx={{
                              flex: "1 1 160px",
                              minWidth: 0,
                            }}
                          />
                        </Box>
                        {dateError && (
                          <Typography variant="caption" color="error" sx={{ display: "block" }}>
                            {dateError}
                          </Typography>
                        )}
                      </Stack>

                      <Divider className="add-from-search-filters-divider" />

                      <Stack spacing={1.25}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          Abstract keywords
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Terms that must appear or must not appear in the abstract.
                        </Typography>
                        <Stack spacing={1.5}>
                          <TextField
                            label="Abstract contains"
                            placeholder="e.g. randomized"
                            value={abstractContains}
                            onChange={(e) => setAbstractContains(e.target.value)}
                            size="small"
                            fullWidth
                          />
                          <TextField
                            label="Abstract does not contain"
                            placeholder="e.g. animal"
                            value={abstractNotContains}
                            onChange={(e) => setAbstractNotContains(e.target.value)}
                            size="small"
                            fullWidth
                          />
                        </Stack>
                      </Stack>

                      <Divider className="add-from-search-filters-divider" />

                      <Stack spacing={1.25}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          Maximum abstracts
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Limit the number of articles retrieved (useful for large or broad searches).
                        </Typography>
                        <TextField
                          label="Number of abstracts"
                          placeholder="e.g. 200"
                          value={numberOfAbstracts}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === "" || /^\d+$/.test(val)) {
                              const num = parseInt(val, 10);
                              if (val === "" || (num >= 1 && num <= 10000)) {
                                setNumberOfAbstracts(val);
                              }
                            }
                          }}
                          size="small"
                          inputProps={{ min: 1, max: 10000, inputMode: "numeric" }}
                          sx={{ maxWidth: 200 }}
                        />
                      </Stack>

                      {studyDesignOptions.length > 0 && (
                        <>
                          <Divider className="add-from-search-filters-divider" />
                          <Stack spacing={1.25}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              Methodology
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Select one or more study designs to include.
                            </Typography>
                            <Box className="add-from-search-filters-methodology">
                              <FormGroup
                                sx={{
                                  display: "flex",
                                  flexDirection: "row",
                                  flexWrap: "wrap",
                                  gap: 0.5,
                                  columnGap: 2,
                                  rowGap: 0.25,
                                }}
                              >
                                {studyDesignOptions.map((option) => (
                                  <FormControlLabel
                                    key={option.value}
                                    control={
                                      <Checkbox
                                        checked={studyDesigns.includes(option.value)}
                                        onChange={() => handleStudyDesignToggle(option.value)}
                                        size="small"
                                      />
                                    }
                                    label={<Typography variant="body2">{option.label}</Typography>}
                                  />
                                ))}
                              </FormGroup>
                            </Box>
                          </Stack>
                        </>
                      )}
                    </Stack>
                  </Box>
                </Box>
              </Collapse>
            </Box>

            <Divider sx={{ mt: 1, mb: 0.5 }} />

            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    sx={{
                      color: "#3b7b3f",
                      "&.Mui-checked": {
                        color: "#3b7b3f",
                      },
                      "&.MuiCheckbox-indeterminate": {
                        color: "#3b7b3f",
                      },
                    }}
                  />
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Select articles retrieved from MedRPT Search
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {selectedSources.size} articles selected
                </Typography>
              </Box>

              {errorMessage && !isArticlesLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 8,
                    gap: 2,
                  }}
                >
                  <Typography variant="body2" color="error">
                    {errorMessage}
                  </Typography>
                </Box>
              ) : isArticlesLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 8,
                    gap: 2,
                  }}
                >
                  <CircularProgress sx={{ color: "#3b7b3f" }} />
                  <Typography variant="body2">
                    {loadingStatus || "Searching for relevant sources..."}
                  </Typography>
                </Box>
              ) : sources.length > 0 ? (
                <Stack spacing={2}>
                  {sources.map((source, index) => {
                    return (
                      <Paper
                        key={index}
                        variant="outlined"
                        sx={{ p: 2, display: "flex", gap: 2 }}
                      >
                        <Checkbox
                          checked={selectedSources.has(index)}
                          onChange={() => handleSourceToggle(index)}
                          sx={{
                            color: "#3b7b3f",
                            "&.Mui-checked": {
                              color: "#3b7b3f",
                            },
                          }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 500, mb: 0.5 }}
                          >
                            {source.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 0.5 }}
                          >
                            {Array.isArray(source.authors)
                              ? source.authors.join(", ")
                              : source.authors || "Unknown authors"}
                          </Typography>
                          {source.journalTitle && (
                            <Box
                              className="journal-title-box"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <MenuBookIcon
                                sx={{
                                  fontSize: 16,
                                  color: "text.secondary",
                                  backgroundColor: "white",
                                  borderRadius: "2px",
                                }}
                              />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {source.journalTitle},
                              </Typography>
                              {source.publicationDate && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {source.publicationDate},
                                </Typography>
                              )}
                              {source.citationCount !== undefined && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {source.citationCount} citations,
                                </Typography>
                              )}
                              {source.doi !== null && (
                                <DoiButton doi={source.doi} />
                              )}
                            </Box>
                          )}
                        </Box>
                      </Paper>
                    );
                  })}
                </Stack>
              ) : null}
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "60vh",
              gap: 2,
            }}
          >
            <CircularProgress sx={{ color: "#3b7b3f" }} />
            <Typography variant="body2">
              {status || "Loading relevant search results"}
            </Typography>
          </Box>
        )}
      </DialogContent>

      {isCompleted && (
        <DialogActions sx={{ p: 2, justifyContent: "flex-end", gap: 2 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              borderColor: "#3b7b3f",
              color: "#3b7b3f",
              borderRadius: "6px",
              fontSize: "14px",
              fontWeight: 500,
              padding: "10px 20px",
              textTransform: "none",
              "&:hover": {
                borderColor: "#3b7b3f",
                backgroundColor: "rgba(59, 123, 63, 0.08)",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddSources}
            variant="contained"
            disabled={selectedSources.size === 0}
            sx={{
              backgroundColor: "#3b7b3f",
              color: "#ffffff",
              borderRadius: "6px",
              fontSize: "14px",
              fontWeight: 500,
              padding: "10px 20px",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#3b7b3f",
                boxShadow: "0 2px 8px #1e3e20",
              },
              "&:disabled": {
                backgroundColor: "#cccccc",
                color: "#666666",
              },
            }}
          >
            Add {selectedSources.size} source(s)
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};
